/*
 * modules.js — the registry the global header search looks through.
 *
 * Groups and access flags mirror the sidebar nav in
 * components/layout/Sidebar.jsx, which is still the source of truth for what
 * renders in the left rail. Add a module here whenever one is added there, or
 * the header search will not find it.
 *
 * access: 'all'      — every signed-in user
 *         'merchant' — hidden from super admins (matches merchantOnly in Sidebar)
 *         'admin'    — super admins only
 */
import {
  Home,
  Wallet,
  History,
  FileText,
  Send,
  Repeat,
  BarChart3,
  Globe,
  Code2,
  CreditCard,
  Settings,
  BookOpen,
  Users,
  KeyRound,
  WalletCards,
  Landmark,
  Scale,
  ScrollText,
  FileBarChart,
  BookUser,
  SlidersHorizontal,
} from 'lucide-react';

const MODULES = [
  // Money & activity
  {
    label: 'Overview',
    path: '/dashboard',
    icon: Home,
    group: 'Dashboard',
    access: 'all',
    keywords: ['dashboard', 'home', 'summary', 'overview'],
  },
  {
    label: 'Balance',
    path: '/dashboard/balance',
    icon: Wallet,
    group: 'Dashboard',
    access: 'all',
    keywords: ['funds', 'available', 'wallet', 'holdings'],
  },
  {
    label: 'Transactions',
    path: '/dashboard/transactions',
    icon: History,
    group: 'Dashboard',
    access: 'all',
    keywords: ['payments', 'activity', 'history', 'tx', 'transfers'],
  },
  {
    label: 'Invoices',
    path: '/dashboard/invoices',
    icon: FileText,
    group: 'Dashboard',
    access: 'all',
    keywords: ['bills', 'invoice', 'requests for payment'],
  },
  {
    label: 'Payouts',
    path: '/dashboard/payouts',
    icon: Send,
    group: 'Dashboard',
    access: 'merchant',
    keywords: ['withdraw', 'withdrawal', 'settlement', 'send funds'],
  },
  {
    label: 'Auto-conversion',
    path: '/dashboard/auto-conversion',
    icon: Repeat,
    group: 'Dashboard',
    access: 'all',
    keywords: ['convert', 'swap', 'ace', 'stablecoin', 'conversion'],
  },

  // Premium
  {
    label: 'Analytics',
    path: '/dashboard/analytics',
    icon: BarChart3,
    group: 'Premium',
    access: 'all',
    keywords: ['insights', 'metrics', 'charts', 'stats'],
  },

  // Configure
  {
    label: 'Networks & Tokens',
    path: '/dashboard/networks',
    icon: Globe,
    group: 'Configure',
    access: 'all',
    keywords: ['chains', 'blockchain', 'tokens', 'assets', 'currencies', 'coins'],
  },
  {
    label: 'Developers',
    path: '/dashboard/developer',
    icon: Code2,
    group: 'Configure',
    access: 'all',
    keywords: ['api', 'api keys', 'webhooks', 'integration', 'sdk', 'docs'],
  },
  {
    label: 'Billing',
    path: '/dashboard/billing',
    icon: CreditCard,
    group: 'Configure',
    access: 'all',
    keywords: ['plan', 'subscription', 'upgrade', 'payment method'],
  },
  {
    label: 'Settings',
    path: '/dashboard/settings',
    icon: Settings,
    group: 'Configure',
    access: 'all',
    keywords: ['profile', 'account', 'security', '2fa', 'preferences', 'password'],
  },
  {
    label: 'Guide',
    path: '/dashboard/guide',
    icon: BookOpen,
    group: 'Configure',
    access: 'all',
    keywords: ['help', 'docs', 'how to', 'tutorial', 'getting started'],
  },

  // Admin
  {
    label: 'Merchants',
    path: '/dashboard/merchants',
    icon: Users,
    group: 'Admin',
    access: 'admin',
    keywords: ['accounts', 'businesses', 'users', 'onboarded'],
  },
  {
    label: 'API Keys',
    path: '/dashboard/payment-links',
    icon: KeyRound,
    group: 'Admin',
    access: 'admin',
    keywords: ['payment links', 'keys', 'secret', 'credentials'],
  },
  {
    label: 'Payout Requests',
    path: '/dashboard/payout-requests',
    icon: CreditCard,
    group: 'Admin',
    access: 'admin',
    keywords: ['payouts', 'approvals', 'withdrawals', 'review', 'queue'],
  },
  {
    label: 'Wallet Requests',
    path: '/dashboard/wallet-change-requests',
    icon: WalletCards,
    group: 'Admin',
    access: 'admin',
    keywords: ['wallet change', 'address change', 'approvals'],
  },

  // Operations
  {
    label: 'Treasury',
    path: '/dashboard/admin/treasury',
    icon: Landmark,
    group: 'Operations',
    access: 'admin',
    keywords: ['sweep', 'hot wallet', 'cold wallet', 'funds'],
  },
  {
    label: 'Solvency',
    path: '/dashboard/admin/solvency',
    icon: Scale,
    group: 'Operations',
    access: 'admin',
    keywords: ['reserves', 'coverage', 'liabilities', 'obligations'],
  },
  {
    label: 'Logs',
    path: '/dashboard/admin/logs',
    icon: ScrollText,
    group: 'Operations',
    access: 'admin',
    keywords: ['audit', 'events', 'activity', 'trail'],
  },
  {
    label: 'Reports',
    path: '/dashboard/admin/reports',
    icon: FileBarChart,
    group: 'Operations',
    access: 'admin',
    keywords: ['exports', 'statements', 'csv', 'scheduled'],
  },
  {
    label: 'Address Book',
    path: '/dashboard/admin/address-book',
    icon: BookUser,
    group: 'Operations',
    access: 'admin',
    keywords: ['addresses', 'whitelist', 'allowlist', 'destinations'],
  },
  {
    label: 'Rules Engine',
    path: '/dashboard/admin/rules',
    icon: SlidersHorizontal,
    group: 'Operations',
    access: 'admin',
    keywords: ['risk', 'limits', 'thresholds', 'policies', 'flags'],
  },
];

/** Modules the given user is allowed to open. */
export const modulesForUser = (isSuperAdmin) =>
  MODULES.filter((m) => {
    if (m.access === 'admin') return isSuperAdmin;
    if (m.access === 'merchant') return !isSuperAdmin;
    return true;
  });

/**
 * Rank modules against a query. Label matches beat keyword matches, and a
 * prefix match beats a match in the middle, so typing "tran" puts
 * Transactions first.
 */
export const searchModules = (query, isSuperAdmin, limit = 6) => {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  return modulesForUser(isSuperAdmin)
    .map((m) => {
      const label = m.label.toLowerCase();
      let score = -1;

      if (label === q) score = 0;
      else if (label.startsWith(q)) score = 1;
      else if (label.includes(q)) score = 2;
      else if (m.keywords.some((k) => k.startsWith(q))) score = 3;
      else if (m.keywords.some((k) => k.includes(q))) score = 4;

      return { module: m, score };
    })
    .filter((r) => r.score >= 0)
    .sort((a, b) => a.score - b.score || a.module.label.localeCompare(b.module.label))
    .slice(0, limit)
    .map((r) => r.module);
};

export default MODULES;
